import { ConnectorHosting } from "@p0security/integrations-app-shared/components";
import { Element, Option } from "@p0security/shared/install/types";

// A copy of github-repositories' connector-name rule, in
// packages/integrations/github-repositories/shared/src/hosting.ts
// (`CONNECTOR_NAME_PATTERN`, `CONNECTOR_NAME_MAX_LENGTH` and
// `connectorNameRules`), kept close to it so the two merge back into one once
// Harbor ships. Change both or neither.

/** A connector name that works for everything the connector's Terraform names
 * after it: a Lambda function with its ECR repository and IAM role. ECR is why
 * a name has at least two characters and no two hyphens in a row. */
const CONNECTOR_NAME_PATTERN = /^[a-z](?:-?[a-z0-9])+$/;

/** The longest connector name: Lambda and IAM cap a function or role name at
 * 64 characters. */
const CONNECTOR_NAME_MAX_LENGTH = 64;

export const INVALID_AWS_CONNECTOR_NAME = `Use 2 to ${CONNECTOR_NAME_MAX_LENGTH} characters for the connector name: lowercase letters, digits and single hyphens, starting with a letter and ending with a letter or digit.`;

/** Whether `name`, ignoring surrounding whitespace, can name a Lambda
 * function, its ECR repository and its IAM role all at once. */
export const isAwsLambdaEcrIamName = (name: unknown): name is string => {
  if (typeof name !== "string") return false;
  const candidate = name.trim();
  return (
    CONNECTOR_NAME_PATTERN.test(candidate) &&
    candidate.length <= CONNECTOR_NAME_MAX_LENGTH
  );
};

// Google's rule for a service account ID: 6 to 30 characters, lowercase
// letters, digits and hyphens, starting with a letter and not ending with a
// hyphen.
const GCP_SERVICE_ACCOUNT_ID_PATTERN = /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/;

export const INVALID_GCP_CONNECTOR_NAME =
  "Use 6 to 30 characters for the connector name: lowercase letters, digits and hyphens, starting with a letter and ending with a letter or digit.";

/** Whether `id`, ignoring surrounding whitespace, can be a Google Cloud
 * service account's ID. */
export const isGcpServiceAccountId = (id: unknown): id is string =>
  typeof id === "string" && GCP_SERVICE_ACCOUNT_ID_PATTERN.test(id.trim());

/** The email of the service account whose ID is the connector's name, in the
 * project `projectId`: the Google Cloud counterpart of the IAM role named
 * `connectorName` on AWS.
 *
 * Each value is trimmed, as P0 stores it. Neither is checked: see
 * {@link isGcpServiceAccountId} for the name.
 */
export const toGcpConnectorServiceAccount = ({
  connectorName,
  projectId,
}: {
  connectorName: string;
  projectId: string;
}) => `${connectorName.trim()}@${projectId.trim()}.iam.gserviceaccount.com`;

const { aws, gcp } = ConnectorHosting.options;

/** {@link ConnectorHosting} for an integration whose own Terraform deploys the
 * connector and names the connector's identity after it: the IAM role
 * `connectorName` on AWS, and the service account with the ID `connectorName`
 * on Google Cloud.
 *
 * So `connectorName` has to work as each name that Terraform gives, and the
 * `gcp` option also holds `connectorServiceAccount`, the email of that service
 * account. P0 sets it, and no request can: wire `gcpConnectorHostingAssembler`
 * as the `gcp` option's assembler, which computes it when the item is created.
 * From then on the install framework refuses a request that changes it, as it
 * does for any field fixed at creation.
 *
 * A custom application's connector is deployed by its owner, under any name
 * and as any account, so it keeps {@link ConnectorHosting}.
 */
export const ConnectorHostingWithServiceAccount = Element({
  ...ConnectorHosting,
  // The rules aren't on `ConnectorHosting` itself. The install framework runs
  // an element's validator at every step and on the hourly install check,
  // where they'd fail a custom application whose connector is named otherwise.
  options: {
    aws: Option({
      ...aws,
      schema: {
        ...aws.schema,
        connectorName: Element({
          ...aws.schema.connectorName,
          validator: async (_id: string, connectorName: string) =>
            isAwsLambdaEcrIamName(connectorName)
              ? undefined
              : INVALID_AWS_CONNECTOR_NAME,
        }),
      },
    }),
    gcp: Option({
      ...gcp,
      schema: {
        ...gcp.schema,
        // The service account's rule is the only one needed: Cloud Run takes
        // any service account ID as a service's name.
        connectorName: Element({
          ...gcp.schema.connectorName,
          validator: async (_id: string, connectorName: string) =>
            isGcpServiceAccountId(connectorName)
              ? undefined
              : INVALID_GCP_CONNECTOR_NAME,
        }),
        // Fixed at creation, or the framework would store whichever account a
        // later request names. Hidden rather than generated, because the
        // new-item form would show a generated field that's fixed at creation.
        connectorServiceAccount: Element({
          label: "Connector service account",
          description:
            "The service account that the connector's Cloud Run service runs as, named after the connector",
          type: "hidden",
          step: "new",
        }),
      },
    }),
  },
});
